import { useState, useRef, useCallback } from "react";
import Draggable from "react-draggable";
import type { DraggableData, DraggableEvent } from "react-draggable";

interface Props {
  id: string;
  title: string;
  defaultPosition: { x: number; y: number };
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
  defaultSize?: { w: number; h: number };
  minSize?: { w: number; h: number };
  maxSize?: { w: number; h: number };
}

const TITLEBAR_H = 28;
const SNAP_DIST  = 28;
const TOP_GUARD  = 40; // height of alert bar
const BASE_Z     = 20;

// Shared z-index counter — clicking a panel brings it to front
let zCounter = BASE_Z;

function loadPos(id: string, fallback: { x: number; y: number }) {
  try {
    const raw = localStorage.getItem(`panel-pos:${id}`);
    if (raw) {
      const p = JSON.parse(raw) as { x: number; y: number };
      // Clamp to viewport in case window was resized since last save
      return {
        x: Math.max(0, Math.min(p.x, window.innerWidth  - 80)),
        y: Math.max(TOP_GUARD, Math.min(p.y, window.innerHeight - 60)),
      };
    }
  } catch {}
  return fallback;
}

function loadSize(id: string, fallback?: { w: number; h: number }) {
  try {
    const raw = localStorage.getItem(`panel-size:${id}`);
    if (raw) return JSON.parse(raw) as { w: number; h: number };
  } catch {}
  return fallback ?? null;
}

function snapToEdge(x: number, y: number, w: number, h: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let nx = x, ny = y;

  if (nx < SNAP_DIST)               nx = 0;
  else if (nx + w > vw - SNAP_DIST) nx = vw - w;

  if (ny < TOP_GUARD + SNAP_DIST)   ny = TOP_GUARD;
  else if (ny + h > vh - SNAP_DIST) ny = vh - h;

  return { x: nx, y: ny };
}

export function FloatingPanel({ id, title, defaultPosition, children, className, onClose, defaultSize, minSize, maxSize }: Props) {
  const nodeRef = useRef<HTMLDivElement>(null);

  // snapKey forces Draggable to re-initialise at the new basePos after an edge-snap
  const [snapKey, setSnapKey]   = useState(0);
  const [basePos, setBasePos]   = useState(() => loadPos(id, defaultPosition));
  const [minimized, setMinimized] = useState(false);

  // Resize state — null means use CSS default sizing
  const [size, setSize] = useState<{ w: number; h: number } | null>(() => loadSize(id, defaultSize));
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  // Focus-to-front
  const [zIndex, setZIndex] = useState(BASE_Z);
  const bringToFront = useCallback(() => {
    const next = ++zCounter;
    setZIndex(next);
  }, []);

  const onStop = (_: DraggableEvent, d: DraggableData) => {
    const node = nodeRef.current;
    const w = node?.offsetWidth  ?? 200;
    const h = node?.offsetHeight ?? TITLEBAR_H;
    const snapped = snapToEdge(d.x, d.y, w, h);

    // If edge-snapping moved the panel, force a re-mount at the snapped coords
    if (snapped.x !== d.x || snapped.y !== d.y) {
      setBasePos(snapped);
      setSnapKey(k => k + 1);
    }

    try {
      localStorage.setItem(`panel-pos:${id}`, JSON.stringify(snapped));
    } catch {}
  };

  const clampSize = useCallback((w: number, h: number) => {
    const lo = minSize ?? { w: 160, h: 80 };
    const hi = maxSize ?? { w: window.innerWidth - 40, h: window.innerHeight - TOP_GUARD - 20 };
    return {
      w: Math.round(Math.max(lo.w, Math.min(w, hi.w))),
      h: Math.round(Math.max(lo.h, Math.min(h, hi.h))),
    };
  }, [minSize, maxSize]);

  const onResizeStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const node = nodeRef.current;
    if (!node) return;
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: node.offsetWidth,
      startH: node.offsetHeight,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onResizeMove = useCallback((e: React.PointerEvent) => {
    const r = resizeRef.current;
    if (!r) return;
    const newW = r.startW + (e.clientX - r.startX);
    const newH = r.startH + (e.clientY - r.startY);
    setSize(clampSize(newW, newH));
  }, [clampSize]);

  const onResizeEnd = useCallback(() => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    // Persist
    setSize(s => {
      if (s) {
        try { localStorage.setItem(`panel-size:${id}`, JSON.stringify(s)); } catch {}
      }
      return s;
    });
  }, [id]);

  // Reset size on double-click of resize handle
  const onResizeReset = useCallback(() => {
    setSize(defaultSize ?? null);
    try { localStorage.removeItem(`panel-size:${id}`); } catch {}
  }, [id, defaultSize]);

  const sizeStyle: React.CSSProperties = size
    ? { width: size.w, height: minimized ? undefined : size.h }
    : {};

  return (
    // key changes only when snapping — causes Draggable to reinitialise at new defaultPosition
    <Draggable
      key={snapKey}
      nodeRef={nodeRef as React.RefObject<HTMLElement>}
      handle=".panel-titlebar"
      defaultPosition={basePos}
      onStop={onStop}
    >
      <div
        ref={nodeRef}
        className={`floating-panel hud-panel${className ? ` fp-${className}` : ""}${resizeRef.current ? " fp-resizing" : ""}${size ? " fp-resized" : ""}`}
        style={{ position: "absolute", zIndex, ...sizeStyle }}
        onPointerDown={bringToFront}
      >
        {/* ── Title bar / drag handle ── */}
        <div className="panel-titlebar">
          <span className="panel-drag-icon" aria-hidden>⠿</span>
          <span className="panel-title">{title}</span>
          <button
            className="panel-minimize-btn"
            onClick={() => setMinimized(m => !m)}
            title={minimized ? "Restore" : "Minimize"}
          >
            {minimized ? "▲" : "▼"}
          </button>
          {onClose && (
            <button
              className="panel-close-btn"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          )}
        </div>

        {/* ── Content ── */}
        {!minimized && (
          <div className="panel-body" style={size ? { flex: 1, minHeight: 0 } : undefined}>
            {children}
          </div>
        )}

        {/* ── Resize handle (bottom-right corner) ── */}
        {!minimized && (
          <div
            className="panel-resize-handle"
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            onDoubleClick={onResizeReset}
          />
        )}
      </div>
    </Draggable>
  );
}
